import React from 'react';
import { Platform } from 'react-native';
import Svg, {
  Path,
  Circle,
  Defs,
  LinearGradient,
  Stop,
} from 'react-native-svg';

// Props for the DynamicColorWheel component.
interface DynamicColorWheelProps {
  colors: string[]; // Array of colors for the wheel segments.
  onColorSelect?: (color: string, isSecondColor?: boolean) => void; // Callback for when a color is selected.
  size?: number; // The diameter of the wheel.
  innerRadiusRatioX?: number; // Horizontal radius of the inner cutout.
  innerRadiusRatioY?: number; // Vertical radius of the inner cutout.
  isPremium?: boolean; // Whether the user has premium access for 2-color selection.
  selectedColors?: string[]; // Currently selected colors for premium users.
}

// On web, use onClick to avoid React Native Responder props (onStartShouldSetResponder, etc.)
// which cause "Unknown event handler" warnings when passed to DOM SVG elements.
// Returns an empty object when no handler is provided to avoid attaching a no-op.
const pressableProps = (onPress: (() => void) | undefined) => {
  if (!onPress) return {};
  return Platform.OS === 'web' ? { onClick: onPress } : { onPress };
};

// Renders an interactive color wheel, which is a solid disc by default.
export default function DynamicColorWheel({
  colors,
  onColorSelect,
  size = 300,
  innerRadiusRatioX = 0,
  innerRadiusRatioY = 0,
  isPremium = false,
  selectedColors = [],
}: DynamicColorWheelProps) {
  const outerRadius = size / 2;
  const innerRadiusX = outerRadius * innerRadiusRatioX;
  const innerRadiusY = outerRadius * innerRadiusRatioY;
  const center = size / 2;

  // Check if a color is a gold metal color
  const isGoldColor = (color: string) => {
    return color === '#FFD700' || color === '#D4AF37' || color === '#DAA520';
  };

  // Check if a color is a silver metal color
  const isSilverColor = (color: string) => {
    return color === '#AAA9AD' || color === '#EAECEC';
  };

  // Check if a color is a bronze metal color
  const isBronzeColor = (color: string) => {
    return color === '#CD7F32' || color === '#B87333';
  };

  // Check if a color is a rose gold metal color
  const isRoseGoldColor = (color: string) => {
    return color === '#E8B4B8' || color === '#F4A6A6';
  };

  // Check if a color is any metal color that should use gradient
  const isMetalColor = (color: string) => {
    return (
      isGoldColor(color) ||
      isSilverColor(color) ||
      isBronzeColor(color) ||
      isRoseGoldColor(color)
    );
  };

  // Get the appropriate fill for a color (gradient or solid)
  const getColorFill = (color: string) => {
    if (isGoldColor(color)) {
      return 'url(#goldGradient)';
    } else if (isSilverColor(color)) {
      return 'url(#silverGradient)';
    } else if (isBronzeColor(color)) {
      return 'url(#bronzeGradient)';
    } else if (isRoseGoldColor(color)) {
      return 'url(#roseGoldGradient)';
    } else {
      return color;
    }
  };

  // Create gradient definition for gold
  const createGoldGradient = () => (
    <Defs>
      <LinearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <Stop offset="0%" stopColor="#FFD700" />
        <Stop offset="25%" stopColor="#FFA500" />
        <Stop offset="50%" stopColor="#FFD700" />
        <Stop offset="75%" stopColor="#FF8C00" />
        <Stop offset="100%" stopColor="#FFD700" />
      </LinearGradient>
    </Defs>
  );

  // Create gradient definition for silver
  const createSilverGradient = () => (
    <Defs>
      <LinearGradient id="silverGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <Stop offset="0%" stopColor="#FFFFFF" />
        <Stop offset="25%" stopColor="#C0C0C0" />
        <Stop offset="50%" stopColor="#E6E6E6" />
        <Stop offset="75%" stopColor="#A8A8A8" />
        <Stop offset="100%" stopColor="#D3D3D3" />
      </LinearGradient>
    </Defs>
  );

  // Create gradient definition for bronze
  const createBronzeGradient = () => (
    <Defs>
      <LinearGradient id="bronzeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <Stop offset="0%" stopColor="#CD7F32" />
        <Stop offset="25%" stopColor="#B87333" />
        <Stop offset="50%" stopColor="#CD7F32" />
        <Stop offset="75%" stopColor="#A0522D" />
        <Stop offset="100%" stopColor="#CD7F32" />
      </LinearGradient>
    </Defs>
  );

  // Create gradient definition for rose gold
  const createRoseGoldGradient = () => (
    <Defs>
      <LinearGradient id="roseGoldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <Stop offset="0%" stopColor="#E8B4B8" />
        <Stop offset="25%" stopColor="#F4A6A6" />
        <Stop offset="50%" stopColor="#E8B4B8" />
        <Stop offset="75%" stopColor="#D4A5A5" />
        <Stop offset="100%" stopColor="#E8B4B8" />
      </LinearGradient>
    </Defs>
  );

  if (!colors || colors.length === 0) {
    return null;
  }

  // Creates the SVG path data for a single "slice" of the wheel.
  const createPath = (startAngle: number, endAngle: number) => {
    const o_x1 = center + outerRadius * Math.cos(startAngle);
    const o_y1 = center + outerRadius * Math.sin(startAngle);
    const o_x2 = center + outerRadius * Math.cos(endAngle);
    const o_y2 = center + outerRadius * Math.sin(endAngle);

    const i_x1 = center + innerRadiusX * Math.cos(startAngle);
    const i_y1 = center + innerRadiusY * Math.sin(startAngle);
    const i_x2 = center + innerRadiusX * Math.cos(endAngle);
    const i_y2 = center + innerRadiusY * Math.sin(endAngle);

    const largeArcFlag = endAngle - startAngle <= Math.PI ? '0' : '1';

    const d = [
      `M ${o_x1} ${o_y1}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${o_x2} ${o_y2}`,
      innerRadiusX > 0 || innerRadiusY > 0
        ? `L ${i_x2} ${i_y2}`
        : `L ${center} ${center}`,
      innerRadiusX > 0 || innerRadiusY > 0
        ? `A ${innerRadiusX} ${innerRadiusY} 0 ${largeArcFlag} 0 ${i_x1} ${i_y1}`
        : '',
      'Z',
    ].join(' ');

    return d;
  };

  // If there's only one color, handle it specially to avoid seams.
  if (colors.length === 1) {
    const color = colors[0];

    // For a solid disc, use a single Circle element to prevent a seam line.
    if (innerRadiusX === 0 && innerRadiusY === 0) {
      return (
        <Svg width={size} height={size} style={{ borderWidth: 0 }}>
          {isMetalColor(color) && createGoldGradient()}
          {isMetalColor(color) && createSilverGradient()}
          {isMetalColor(color) && createBronzeGradient()}
          {isMetalColor(color) && createRoseGoldGradient()}
          <Circle
            cx={center}
            cy={center}
            r={outerRadius}
            fill={getColorFill(color)}
            {...pressableProps(() => onColorSelect?.(color))}
          />
        </Svg>
      );
    }

    // For a ring, draw with a single complete path to avoid seams.
    const createRingPath = () => {
      const outerCircle = `M ${
        center - outerRadius
      } ${center} A ${outerRadius} ${outerRadius} 0 1 1 ${
        center + outerRadius
      } ${center} A ${outerRadius} ${outerRadius} 0 1 1 ${center - outerRadius} ${center}`;
      const innerCircle = `M ${
        center - innerRadiusX
      } ${center} A ${innerRadiusX} ${innerRadiusY} 0 1 1 ${
        center + innerRadiusX
      } ${center} A ${innerRadiusX} ${innerRadiusY} 0 1 1 ${center - innerRadiusX} ${center}`;
      return `${outerCircle} ${innerCircle}`;
    };

    return (
      <Svg width={size} height={size} style={{ borderWidth: 0 }}>
        {isMetalColor(color) && createGoldGradient()}
        {isMetalColor(color) && createSilverGradient()}
        {isMetalColor(color) && createBronzeGradient()}
        {isMetalColor(color) && createRoseGoldGradient()}
        <Path
          d={createRingPath()}
          fill={getColorFill(color)}
          fillRule="evenodd"
          {...pressableProps(() => onColorSelect?.(color))}
        />
      </Svg>
    );
  }

  const slice = (2 * Math.PI) / colors.length;

  return (
    <Svg
      width={size}
      height={size}
      style={{ transform: [{ rotate: '-90deg' }], borderWidth: 0 }} // Start the wheel from the top.
    >
      {createGoldGradient()}
      {createSilverGradient()}
      {createBronzeGradient()}
      {createRoseGoldGradient()}
      {colors.map((color, i) => {
        const startAngle = slice * i;
        const endAngle = slice * (i + 1);
        const path = createPath(startAngle, endAngle);
        const isSelected = selectedColors.includes(color);

        return (
          <Path
            key={i}
            d={path}
            fill={getColorFill(color)}
            {...pressableProps(() => {
              if (isPremium) {
                // For premium users, handle 2-color selection
                if (isSelected) {
                  // If color is already selected, deselect it
                  onColorSelect?.(color, false);
                } else if (selectedColors.length < 2) {
                  // If less than 2 colors selected, add this one
                  onColorSelect?.(color, selectedColors.length === 1);
                } else {
                  // If 2 colors already selected, replace the second one
                  onColorSelect?.(color, true);
                }
              } else {
                // For free users, single color selection
                onColorSelect?.(color);
              }
            })}
          />
        );
      })}
    </Svg>
  );
}
